import { FC, FormEvent, useEffect, useMemo, useState } from 'react'

import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { listPassports } from 'pages/avia/purchase-form/lib/getAvailableDocument'
import { PassengersValues } from 'pages/avia/purchase-form/model'

import { DOCUMENT } from 'entities/user/module'

import { PassportMessage } from 'shared/api/PassportInfo'
import { AVAILABLE_ID_PASSPORT, MAX_YEARS_DURATIONS } from 'shared/constants'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { REGEX_NAME } from 'shared/constants/regex'
import { addDate, dateFormat } from 'shared/lib/dateFormat'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types.ts'
// import { CheckBox } from 'shared/ui/check-box'
import { DatePickerWithLabel } from 'shared/ui/date-picker-label'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { GenderSelect } from 'shared/ui/gender-select'
import { InputWithLabel } from 'shared/ui/input-label'
import { SelectWithLabel } from 'shared/ui/select-label'

import { FormPassengersValues } from '../../models/form-passengers'

import { getRegexByAgeAndCitizenship } from './lib/getRegexByAgeAndCitizenship'
import { DataForPurchase } from './model'
import s from './styles.module.scss'

interface Props {
  formInformation: DataForPurchase
  getPassportInfo: (
    citizenship: string,
    documentType: string,
  ) => PassportMessage | undefined
}

export const PurchaseForm: FC<Props> = ({
  formInformation,
  getPassportInfo,
}) => {
  const { t } = useTranslation()

  const { openDialog } = useDialogBottomSheet()

  const [popupInfo, setPopupInfo] = useState<PassportMessage>()

  const { keyPassenger, citizenship } = formInformation

  const {
    control,
    resetField,
    formState: { errors },
  } = useFormContext<FormPassengersValues>()

  const { age, index } = keyPassenger

  const formName = `${age}.${index}` as const

  const onChangeInput = (event: FormEvent<HTMLInputElement>) => {
    return event?.currentTarget?.value?.toUpperCase().replace(REGEX_NAME, '')
  }

  const onChangeInputSerialNumber = (
    event: FormEvent<HTMLInputElement>,
    regex: RegExp,
  ): string => {
    return event?.currentTarget?.value?.toUpperCase().replace(regex, '')
  }

  const showDocumentPopup = () => {
    openDialog({
      child: (
        <div className={s.dialog}>
          <p className={clsx(s.title, 'bold-20')}>
            {t('purchase_page.serial_number_pass')}
          </p>

          <p className={s.text}>{popupInfo?.text}</p>
        </div>
      ),
    })
  }

  const handleCitizenshipPassportChange = (
    citizenship: string,
    documentType: string,
  ) => {
    const response = getPassportInfo(citizenship, documentType)

    setPopupInfo(response)
  }

  const formValue = useWatch({
    control,
    name: `${formName}`,
  }) as PassengersValues

  // const hasMiddleName = useWatch({
  //   control,
  //   name: `${formName}.doesMiddleName`,
  // })

  const passport = useWatch({
    control,
    name: `${formName}.passport`,
  }) as string

  const selectedCitizenship = useWatch({
    control,
    name: `${formName}.citizenship`,
  }) as string

  const maxDateExpiration = addDate({
    count: MAX_YEARS_DURATIONS,
    type: 'year',
  })

  const serialNumberRegex = useMemo(() => {
    return getRegexByAgeAndCitizenship({
      age,
      passport,
      citizenship: selectedCitizenship,
    })
  }, [passport, age, selectedCitizenship])

  useEffect(() => {
    if (selectedCitizenship?.length && passport?.length) {
      handleCitizenshipPassportChange(selectedCitizenship, passport)
    }
  }, [])

  return (
    <div
      className={s.wrapper}
      key={`${formValue?.id || ''}${formValue?.citizenship || ''}${formValue?.passport || ''}`}
    >
      <Controller
        control={control}
        name={`${age}.${index}.lastName`}
        rules={{ required: true, minLength: 2, maxLength: 30 }}
        render={({ field }) => (
          <InputWithLabel
            ref={field.ref}
            value={field?.value}
            label={t('form.last_name')}
            error={errors?.[age]?.[index]?.lastName}
            onChange={(event) => field?.onChange(onChangeInput(event))}
          />
        )}
      />

      <Controller
        control={control}
        name={`${age}.${index}.firstName`}
        rules={{ required: true, minLength: 2, maxLength: 30 }}
        render={({ field }) => (
          <InputWithLabel
            ref={field.ref}
            value={field?.value}
            label={t('form.first_name')}
            error={errors?.[age]?.[index]?.firstName}
            onChange={(event) => field?.onChange(onChangeInput(event))}
          />
        )}
      />

      {/* <Controller
        control={control}
        name={`${age}.${index}.doesMiddleName`}
        render={({ field }) => (
          <CheckBox
            isChecked={!!field?.value}
            title={t('does_middle_name')}
            onClick={(value) => {
              field.onChange(value)
            }}
          />
        )}
      />

      <Controller
        control={control}
        name={`${age}.${index}.middleName`}
        rules={{ required: hasMiddleName, minLength: 2, maxLength: 30 }}
        render={({ field }) => (
          <div className={clsx(!hasMiddleName && s.disabledField)}>
            <Input
              disabled={!hasMiddleName}
              ref={field.ref}
              value={field?.value}
              placeholder={t('form.middle_name')}
              error={errors?.[age]?.[index]?.middleName}
              onChange={(event) => field?.onChange(onChangeInput(event))}
            />
          </div>
        )}
      /> */}

      <Controller
        name={`${age}.${index}.gender`}
        control={control}
        rules={{ required: t('select_gender') }}
        render={({ field }) => (
          <GenderSelect
            ref={field.ref}
            value={field.value}
            onBlur={field.onBlur}
            onChange={field.onChange}
            error={errors?.[age]?.[index]?.gender}
          />
        )}
      />

      <Controller
        name={`${age}.${index}.dateOfBirth`}
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <DatePickerWithLabel
            {...field}
            error={errors?.[age]?.[index]?.dateOfBirth}
            label={t('birth_day')}
            value={field.value ? new Date(field.value) : undefined}
            onChange={(date) => {
              field.onChange(dateFormat(date, DATE_FORMAT_LIST.YEAR_FULL_DATE))
            }}
          />
        )}
      />

      <Controller
        name={`${formName}.citizenship`}
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <SelectWithLabel
            {...field}
            key={field.value}
            ref={field?.ref}
            value={field?.value}
            options={citizenship}
            label={t('citizenship')}
            onChange={(option) => {
              const isAvailableCitizenship = AVAILABLE_ID_PASSPORT.includes(
                String(option.value).toLocaleLowerCase(),
              )

              field.onChange(option.value)

              handleCitizenshipPassportChange(option.value, passport)

              if (
                !isAvailableCitizenship &&
                (passport === DOCUMENT.ID ||
                  passport === DOCUMENT.BIRTH_CERTIFICATE)
              ) {
                resetField(`${formName}.passport`)
                resetField(`${formName}.serialDocuments`)
                resetField(`${formName}.documentExpirationDate`)
              }

              resetField(`${formName}.serialDocuments`)
            }}
            error={errors?.[age]?.[index]?.citizenship}
          />
        )}
      />

      <Controller
        name={`${formName}.passport`}
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <SelectWithLabel
            {...field}
            ref={field?.ref}
            key={field.value}
            value={field.value || ''}
            options={listPassports?.map((opt) => ({
              ...opt,
              name: t(`${opt.name}`),
            }))}
            label={t('type_passport')}
            error={errors?.[age]?.[index]?.passport}
            onChange={(option) => {
              field.onChange(option?.value)

              handleCitizenshipPassportChange(
                selectedCitizenship,
                option?.value,
              )

              if (String(passport)?.length && passport !== option?.value) {
                resetField(`${formName}.serialDocuments`, {
                  defaultValue: '' as never,
                })
                resetField(`${formName}.documentExpirationDate`, {
                  defaultValue: '' as never,
                })
              }
            }}
          />
        )}
      />

      <Controller
        control={control}
        name={`${formName}.serialDocuments`}
        rules={{ required: true, minLength: 2, maxLength: 30 }}
        render={({ field }) => (
          <InputWithLabel
            ref={field.ref}
            value={String(field?.value || '')}
            label={t('serial_documents')}
            error={errors?.[age]?.[index]?.serialDocuments}
            onChange={(event) => {
              return field?.onChange(
                onChangeInputSerialNumber(event, serialNumberRegex),
              )
            }}
            rightIcon={
              popupInfo && (
                <AppIcon
                  id={AllIcons.INFO}
                  height={24}
                  width={24}
                  onClick={showDocumentPopup}
                />
              )
            }
          />
        )}
      />

      <Controller
        name={`${age}.${index}.documentExpirationDate`}
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <DatePickerWithLabel
            {...field}
            ref={field.ref}
            key={field.value}
            minDate={new Date()}
            maxDate={maxDateExpiration}
            rangeYear={MAX_YEARS_DURATIONS}
            label={t('expiration_date')}
            value={field.value ? new Date(field.value) : undefined}
            error={errors?.[age]?.[index]?.documentExpirationDate}
            onChange={(date) => {
              field.onChange(dateFormat(date, DATE_FORMAT_LIST.YEAR_FULL_DATE))
            }}
          />
        )}
      />
    </div>
  )
}
